/**
 * useAddUser Hook
 *
 * React Query mutation hook for adding a user to a group.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { groupService } from '../../../api/group.service';
import type { GroupAddUserDTO } from '../../../types/group.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface AddUserVariables {
  groupId: string;
  data: GroupAddUserDTO;
}

interface AddUserResponse {
  message: string;
  groupId: string;
  userId: string;
  updatedAt: string;
}

/**
 * Hook for adding a user to a group
 */
export const useAddUser = (
  tenantId: string
): UseMutationResult<AddUserResponse, Error, AddUserVariables> => {
  const queryClient = useQueryClient();

  return useMutation<AddUserResponse, Error, AddUserVariables>({
    mutationFn: ({ groupId, data }) => {
      console.log('[useAddUser] Adding user to group:', groupId, data.userId);
      return groupService.addUser(tenantId, groupId, data);
    },
    onSuccess: (_, variables) => {
      console.log('[useAddUser] User added successfully');
      // Invalidate group to refresh user list
      queryClient.invalidateQueries({ queryKey: ['group', tenantId, variables.groupId] });
      // Invalidate groups list
      queryClient.invalidateQueries({ queryKey: ['groups', tenantId] });
      // Invalidate user to refresh their groups array
      queryClient.invalidateQueries({ queryKey: ['user', tenantId, variables.data.userId] });
      // Invalidate users list
      queryClient.invalidateQueries({ queryKey: ['users', tenantId] });
    },
    onError: (error) => {
      console.error('[useAddUser] Failed to add user:', getErrorMessage(error));
    },
  });
};
